import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
  TablePagination,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CategoryIcon from "@mui/icons-material/Category";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { categoryService } from "../services";
import { formatDateTime } from "../utils/dateFormat";
import CachedImage from "../components/CachedImage";
import ManagementSearchBar from "../components/shared/ManagementSearchBar";
import TableLoadingRow from "../components/shared/TableLoadingRow";
import TableEmptyState from "../components/shared/TableEmptyState";

const CategoryManagementPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [debouncedSearch, setDebouncedSearch] = useState("");

  const applySearchQuery = useCallback((q) => {
    setDebouncedSearch(q);
    setPage(0);
  }, []);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      const data = await categoryService.getCategories(params);
      if (data && data.items) {
        setCategories(data.items);
        setTotalItems(data.pagination?.total || 0);
      } else {
        setCategories(data || []);
        setTotalItems(data?.length || 0);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (category) => {
    setSelectedCategory(category);
    setEditFormData({
      name: category.name || "",
      image_url: category.image_url || "",
      priority: category.priority ?? "",
      trending: category.trending ?? false,
    });
    setEditDialogOpen(true);
  };

  const handleInlineTrendingToggle = async (category) => {
    const nextTrending = !category.trending;
    if (nextTrending && !category.priority) {
      // Can't enable trending without a priority — open edit dialog prefilled
      setSelectedCategory(category);
      setEditFormData({
        name: category.name || "",
        image_url: category.image_url || "",
        priority: "",
        trending: true,
      });
      setActionError("Please set a priority before enabling trending.");
      setEditDialogOpen(true);
      return;
    }
    try {
      await categoryService.updateCategory(category.id, {
        trending: nextTrending,
      });
      setSuccessMessage(
        `Trending ${nextTrending ? "enabled" : "disabled"} for "${category.name}"`,
      );
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchCategories();
    } catch (err) {
      console.error("Error toggling trending:", err);
      setSuccessMessage(null);
      setActionError(
        err.response?.data?.message || "Failed to update trending status",
      );
    }
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedCategory(null);
    setEditFormData({});
    setActionError(null);
  };

  const handleEditChange = (field, value) => {
    setEditFormData({ ...editFormData, [field]: value });
  };

  const handleSaveEdit = async () => {
    try {
      setActionLoading(true);
      setActionError(null);

      if (!selectedCategory?.id) {
        setActionError("Category ID is required for update");
        return;
      }

      if (editFormData.trending && !editFormData.priority) {
        setActionError("Priority is required when trending is enabled.");
        return;
      }

      const payload = {
        ...editFormData,
        priority:
          editFormData.priority !== "" ? Number(editFormData.priority) : null,
      };

      await categoryService.updateCategory(selectedCategory.id, payload);
      setSuccessMessage("Category updated successfully");
      setTimeout(() => setSuccessMessage(null), 3000);
      handleEditClose();
      fetchCategories();
    } catch (err) {
      console.error("Error updating category:", err);
      setActionError(
        err.response?.data?.message || "Failed to update category",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setActionLoading(true);
      setActionError(null);
      const response = await categoryService.deleteCategory(
        selectedCategory.id,
      );

      if (response?.success || response?.message) {
        setSuccessMessage(response.message || "Category deleted successfully");
        setTimeout(() => setSuccessMessage(null), 3000);
        setDeleteConfirmOpen(false);
        setSelectedCategory(null);
        fetchCategories();
      } else {
        setActionError(response?.message || "Unknown error occurred");
      }
    } catch (err) {
      console.error("Error deleting category:", err);
      setActionError(
        err.response?.data?.message || "Failed to delete category",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setSelectedCategory(null);
    setActionError(null);
  };

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", color: "#1a1a1a", mb: 1 }}
          >
            Manage Categories
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and maintain product categories
          </Typography>
        </Box>
        <Tooltip title="Refresh Categories">
          <IconButton
            onClick={fetchCategories}
            disabled={loading}
            color="primary"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <ManagementSearchBar
        label="Search by Name or Slug"
        placeholder="Enter category name or slug..."
        onApplySearchQuery={applySearchQuery}
      />

      <Card
        sx={{
          borderRadius: 2,
          boxShadow:
            "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
            <Table>
              <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: 70 }}>
                    Image
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Slug</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Image URL</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    Priority
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    Trending
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Last Modified
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableLoadingRow colSpan={8} />
                ) : categories.length === 0 ? (
                  <TableEmptyState
                    colSpan={8}
                    message="No categories found. Upload a CSV to get started."
                  />
                ) : (
                  categories.map((category) => (
                    <TableRow key={category.id} hover>
                      <TableCell sx={{ width: 70 }}>
                        {category.image_url ? (
                          <CachedImage
                            src={category.image_url}
                            alt={category.name}
                            style={{
                              height: "52px",
                              width: "52px",
                              objectFit: "cover",
                              borderRadius: "6px",
                              display: "block",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: "52px",
                              width: "52px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "#f5f5f5",
                              borderRadius: "6px",
                            }}
                          >
                            <CategoryIcon
                              sx={{ color: "#e90000", opacity: 0.6 }}
                            />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {category.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            bgcolor: "#f5f5f5",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            display: "inline-block",
                          }}
                        >
                          {category.slug}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 320,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {category.image_url || <em>Not provided</em>}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {category.priority != null ? (
                          <Chip
                            label={category.priority}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: "#f0f4ff",
                              color: "#1976d2",
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            category.trending
                              ? "Disable trending"
                              : category.priority
                                ? "Enable trending"
                                : "Set priority first to enable trending"
                          }
                        >
                          <span>
                            <Switch
                              checked={!!category.trending}
                              onChange={() =>
                                handleInlineTrendingToggle(category)
                              }
                              size="small"
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: "#e90000",
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                  { bgcolor: "#e90000" },
                              }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(category.updated_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          <Tooltip title="Edit Category">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(category)}
                              sx={{ color: "#1976d2" }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Category">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(category)}
                              sx={{ color: "#d32f2f" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, bgcolor: "#f8f9fb" }}>
          Edit Category
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionError && (
            <Alert
              severity="error"
              onClose={() => setActionError(null)}
              sx={{ mb: 2 }}
            >
              {actionError}
            </Alert>
          )}

          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}
          >
            <TextField
              label="Category Name"
              value={editFormData.name || ""}
              onChange={(e) => handleEditChange("name", e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              required
            />

            <TextField
              label="Image URL"
              value={editFormData.image_url || ""}
              onChange={(e) => handleEditChange("image_url", e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              placeholder="https://example.com/category.jpg"
            />

            <TextField
              label="Priority"
              value={editFormData.priority ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || (/^\d+$/.test(val) && Number(val) >= 1)) {
                  handleEditChange("priority", val);
                }
              }}
              fullWidth
              size="small"
              disabled={actionLoading}
              type="number"
              inputProps={{ min: 1 }}
              placeholder="e.g. 1"
              helperText={
                editFormData.trending && !editFormData.priority
                  ? "Required when trending is enabled."
                  : "Positive integer. Leave empty if not applicable."
              }
              error={!!editFormData.trending && !editFormData.priority}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid",
                borderColor:
                  editFormData.trending && !editFormData.priority
                    ? "error.main"
                    : "divider",
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Trending
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Show this category in the trending section
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editFormData.trending}
                    onChange={(e) =>
                      handleEditChange("trending", e.target.checked)
                    }
                    disabled={actionLoading}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#e90000",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        { bgcolor: "#e90000" },
                    }}
                  />
                }
                label=""
                sx={{ m: 0 }}
              />
            </Box>

            <TextField
              label="Slug"
              value={selectedCategory?.slug || ""}
              fullWidth
              size="small"
              disabled
              helperText="Slug is auto-generated from category name (lowercase with '-' separator)."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, mt: 2 }}>
          <Button
            onClick={handleEditClose}
            disabled={actionLoading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={
              actionLoading ||
              !editFormData.name?.trim() ||
              (!!editFormData.trending && !editFormData.priority)
            }
            sx={{ bgcolor: "#e90000", "&:hover": { bgcolor: "#cc0000" } }}
          >
            {actionLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#d32f2f" }}>
          Delete Category
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete the category{" "}
            <strong>{selectedCategory?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>

          {actionError && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              onClose={() => setActionError(null)}
            >
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={actionLoading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={actionLoading || !!actionError}
            startIcon={
              actionLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
            sx={{ bgcolor: "#e90000", fontWeight: "bold" }}
          >
            {actionLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
};

export default CategoryManagementPage;
